import { bundleActions } from '@/actions/bundleActions';
import ServiceListCardWrapper from '@/components/elements/Card/ServiceListCard/ServiceListCardWrapper';
import { LinkButton } from '@/components/elements/forms/buttons';
import { ButtonProps } from '@/components/elements/forms/buttons/Button/Button';
import { SCREEN_NAME } from '@/constants/analytics';
import { classnames, getBundleValidity, url } from '@/helpers';
import { buyBundleFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { themed } from '@/helpers/theme';
import { useAppSelector } from '@/hooks';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { Place, PlaceBundle } from '@/types/state/shared';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import LegacyBundleReadMore from './LegacyBundleReadMore';

type PlaceBundleWithHighlight = PlaceBundle & { highlightedName?: React.ReactNode };

export type PlaceBundleService = {
  mpId: number;
  name: string;
  quantity: number;
};

type BundleProps = {
  place: Place;
  bundle: PlaceBundleWithHighlight;
};

const LegacyBundle = ({ bundle, place }: BundleProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useAppSelector((state) => state.users)?.user;
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const { isMobileView } = useMobileView();

  useEffect(() => {
    const selectedBundleId = parseInt(url.returnGetParameter('sb'));
    if (bundle.id === selectedBundleId) {
      setShowMoreInfo(true);
    }
  }, []);

  const handleOnCloseMoreInfo = () => {
    history.replace({ search: url.changeGetParameter('sb') });
  };

  const handleOnOpenMoreInfo = () => {
    history.replace({ search: url.changeGetParameter('sb', bundle.id) });
  };

  const handleBuyClick = () => {
    dispatch(bundleActions.pickBundleToCheckout({ bundle, place }));
    buyBundleFlowTrackingSession.initiate(place, user, SCREEN_NAME.COMPANY_DETAILS, { bundle_template_id: bundle.id });
  };

  const getBuyButton = (block?: boolean, size?: ButtonProps['size']) => {
    return (
      <LinkButton
        className="min-w-[75px]"
        block={block}
        size={size}
        variant="primary"
        to="/bundles/checkout"
        onClick={handleBuyClick}>
        {_s('buy')}
      </LinkButton>
    );
  };

  const getReadMoreButton = () => {
    return (
      <Fragment>
        {/* spacer to avoid elements going under fixed bottom card */}
        {isMobileView && <div className="h-[128px]" />}
        {/* spacer to avoid elements going under fixed bottom card */}
        <div
          className={classnames(isMobileView ? 'fixed left-0 right-0 mt-auto' : '-mx-2xl -mb-2xl sticky', 'bottom-0')}>
          <div className={`p-lg w-full bg-white`}>{getBuyButton(true, 'md')}</div>
        </div>
      </Fragment>
    );
  };

  const getBundlePriceFormatted = () => {
    if (bundle.finalPrice && bundle.finalPrice !== bundle.price) {
      return (
        <span className={themed('text-information', 'text-green-sm')}>
          {`${bundle.finalPriceLabel} `}
          <s className="text-black-600">{`(${bundle.priceLabel})`}</s>
        </span>
      );
    }

    return <span>{bundle.priceLabel}</span>;
  };

  return (
    <li>
      <ServiceListCardWrapper
        key={bundle.id}
        id={bundle.id}
        bundle={{ quantity: bundle.service.quantity, validity: getBundleValidity(bundle.validMonths) }}
        price={{ value: [bundle.price], type: 'standard' }}
        name={bundle.name}
        highlightedName={bundle.highlightedName}
        duration={bundle.durationLabel}
        discount={bundle.finalPrice ? { amounts: [bundle.price - bundle.finalPrice] } : undefined}
        onRowClick={() => {
          handleBuyClick();
          history.push('/bundles/checkout');
        }}
        readMore={
          <LegacyBundleReadMore
            bundle={bundle}
            place={place}
            bundlePrice={getBundlePriceFormatted()}
            actionButton={getReadMoreButton()}
            showMoreInfo={showMoreInfo}
            handleOnOpenMoreInfo={handleOnOpenMoreInfo}
            handleOnCloseMoreInfo={handleOnCloseMoreInfo}
          />
        }
        rightSlot={
          <LinkButton
            size={isMobileView ? 'md' : 'sm'}
            variant="primary"
            to="/bundles/checkout"
            onClick={(e) => {
              e.stopPropagation();
              handleBuyClick();
            }}>
            {_s('buy')}
          </LinkButton>
        }
      />
    </li>
  );
};

export default LegacyBundle;
